import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AdminTracks.module.css';
import { logger } from '../config';
import { fetchAdminCategories, getToken, handleApiError } from '../utils/api';
import { SuccessToast, useSuccessToast } from './shared/SuccessToast';
import {
  AdminCategoriesHeader,
  AdminTracksLoadingState,
  ContentIcon,
  DeleteIcon,
  EditIcon,
  categoryId,
  categoryName,
  getAdminSessionInfo,
  getCategoryCoverUrl,
  sortCatalogCategories,
} from './AdminCategories/helpers';
import {
  CreateCategoryModal,
  DeleteCategoryModal,
  EditCategoryModal,
} from './AdminCategories/modals';

function CategoryCard({ category, onManageContent, onEdit, onDelete }) {
  const coverUrl = getCategoryCoverUrl(category);
  const name = categoryName(category) || 'Untitled';
  const trackCount = Number(category?.content_count) || 0;
  const [coverFailed, setCoverFailed] = useState(false);
  const showCover = Boolean(coverUrl) && !coverFailed;

  useEffect(() => {
    setCoverFailed(false);
  }, [coverUrl]);

  return (
    <div className={styles['track-card']} role="listitem">
      <div className={styles['track-cover']}>
        {showCover ? (
          <img
            className={styles['track-cover-img']}
            src={coverUrl}
            alt={`${name} cover`}
            onError={() => setCoverFailed(true)}
          />
        ) : (
          <div className={styles['track-cover-empty']}>No cover</div>
        )}
      </div>
      <div className={styles['track-body']}>
        <div className={styles['track-title-row']}>
          <div className={styles['track-title-block']}>
            <p className={styles['category']}>
              {`${trackCount} ${trackCount === 1 ? 'Quick Richuel' : 'Quick Richuels'}`}
            </p>
            <h3 className={styles['track-title']}>{name}</h3>
          </div>
          <div className={styles['track-actions']}>
            <button
              type="button"
              className={styles['icon-button']}
              onClick={() => onManageContent(category)}
              title="Manage Quick Richuels"
              aria-label="Manage Quick Richuels"
            >
              <ContentIcon />
            </button>
            <button type="button" className={styles['icon-button']} onClick={() => onEdit(category)} title="Edit" aria-label="Edit">
              <EditIcon />
            </button>
            <button
              type="button"
              className={`${styles['icon-button']} ${styles['icon-danger']}`}
              onClick={() => onDelete(category)}
              title="Delete"
              aria-label="Delete"
            >
              <DeleteIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const AdminCategories = () => {
  const navigate = useNavigate();

  const [adminInfo, setAdminInfo] = useState({ email: '', fullName: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [deleteCategory, setDeleteCategory] = useState(null);
  const { message: successMessage, isExiting: successMessageExiting, showToast: showSuccessMessage } = useSuccessToast();

  const sortedCategories = useMemo(() => sortCatalogCategories(categories), [categories]);

  const refreshCategories = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        handleApiError(new Error('No token found'), navigate, true);
        return;
      }
      const result = await fetchAdminCategories(navigate);
      if (!result) {
        setError('Failed to load categories.');
        setCategories([]);
        return;
      }
      setCategories(Array.isArray(result.categories) ? result.categories : []);
    } catch (e) {
      logger.error('Error loading categories:', e);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      handleApiError(new Error('No token found'), navigate, true);
      return;
    }
    setAdminInfo(getAdminSessionInfo());
    refreshCategories();
  }, [navigate, refreshCategories]);

  const handleManageContent = useCallback((category) => {
    const id = categoryId(category);
    if (!id) return;
    navigate(`/admin-category-contents?categoryId=${encodeURIComponent(id)}`);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/admin-login');
  };

  const handleCreated = useCallback((message) => {
    setCreateOpen(false);
    showSuccessMessage(message || 'Category created successfully.');
    refreshCategories();
  }, [refreshCategories, showSuccessMessage]);

  const handleSaved = useCallback((message) => {
    setEditCategory(null);
    showSuccessMessage(message || 'Category updated successfully.');
    refreshCategories();
  }, [refreshCategories, showSuccessMessage]);

  const handleDeleted = useCallback((message) => {
    setDeleteCategory(null);
    showSuccessMessage(message || 'Category deleted successfully.');
    refreshCategories();
  }, [refreshCategories, showSuccessMessage]);

  return (
    <div className={styles['admin-tracks-container']}>
      <AdminCategoriesHeader
        adminInfo={adminInfo}
        onBack={() => navigate('/admin-dashboard')}
        onLogout={handleLogout}
      />

      <main className={styles['admin-main-content']} id="admin-categories-main">
        <SuccessToast message={successMessage} isExiting={successMessageExiting} />
        {error && (
          <div className={styles['error-message']} role="alert">
            {error}
          </div>
        )}

        <div className={styles['tracks-toolbar']}>
          <div className={styles['tracks-toolbar-left']}>
            {!loading && (
              <p className={styles['tracks-count']} aria-live="polite">
                <span className={styles['tracks-count-number']}>{sortedCategories.length}</span>
                {' '}
                {sortedCategories.length === 1 ? 'category' : 'categories'}
              </p>
            )}
          </div>
          <div className={styles['tracks-toolbar-right']}>
            <button
              type="button"
              className={styles['create-button']}
              onClick={() => setCreateOpen(true)}
            >
              <span className={styles['btn-label']}>+ Create Category</span>
            </button>
          </div>
        </div>

        {loading ? (
          <AdminTracksLoadingState message="Loading categories…" />
        ) : sortedCategories.length === 0 ? (
          <div className={styles['empty-state']}>
            <p className={styles['empty-state-title']}>No categories yet</p>
            <p className={styles['empty-state-copy']}>Create a category to get started.</p>
          </div>
        ) : (
          <div className={styles['tracks-grid']} role="list">
            {sortedCategories.map((category) => (
              <CategoryCard
                key={`${category.id || categoryName(category)}-${category.cover_image || ''}`}
                category={category}
                onManageContent={handleManageContent}
                onEdit={setEditCategory}
                onDelete={setDeleteCategory}
              />
            ))}
          </div>
        )}
      </main>

      {createOpen && (
        <CreateCategoryModal
          navigate={navigate}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      )}
      {editCategory && (
        <EditCategoryModal
          category={editCategory}
          navigate={navigate}
          onClose={() => setEditCategory(null)}
          onSaved={handleSaved}
        />
      )}
      {deleteCategory && (
        <DeleteCategoryModal
          category={deleteCategory}
          navigate={navigate}
          onClose={() => setDeleteCategory(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};

export default AdminCategories;
